import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Job } from '@core/generated/gql-types';
import dayjs from 'dayjs';
import { SortField, SortDirection } from './useJobsTable';
import { useClientStore } from '@core/client/useClientStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { ResizeHandle } from '@/components/shared/ResizeHandle';
import { useColumnResize } from './useColumnResize';
import { useJobUtils } from './useJobUtils';
import { useTenantId } from '@/contexts/TenantContext';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { formatJobTime12h } from './utils/jobTime.util';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
} from '@core/util/jobScheduleBounds.util';

/** Let Cmd/Ctrl-click, shift-click, and non–primary-button clicks follow the real URL (new tab/window). */
function shouldUseDefaultLinkNavigation(
  e: React.MouseEvent<HTMLAnchorElement>,
): boolean {
  return (
    e.defaultPrevented ||
    e.button !== 0 ||
    e.ctrlKey ||
    e.metaKey ||
    e.shiftKey ||
    e.altKey
  );
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  itemsPerPage: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  hasMore?: boolean;
}

interface JobsTableProps {
  jobs: Job[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onJobClick: (jobId: string) => void;
  showClientColumn?: boolean;
  showMarginColumn?: boolean;
  showCandidateColumn?: boolean;
  showApplicationsColumn?: boolean;
  /** When set, missing assignees are loaded via fetchWorkersForClient instead of tenant. */
  workersClientId?: string;
  pagination: PaginationProps;
  isLoading?: boolean;
}

const JobsTable: React.FC<JobsTableProps> = ({
  jobs,
  sortField,
  sortDirection,
  onSort,
  onJobClick,
  showClientColumn = true,
  showMarginColumn = true,
  showCandidateColumn = true,
  showApplicationsColumn = true,
  workersClientId,
  pagination,
  isLoading,
}) => {
  const { clients } = useClientStore();
  const {
    getWorkerById,
    fetchWorkersForTenant,
    fetchWorkersForClient,
    workers,
  } = useWorkerStore();
  const tenantId = useTenantId();
  const { getTerm } = useTemplateStore();
  const { classificationGroups, getClassificationById } =
    useClassificationStore();

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↑↓';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const { getStatusBadgeClass } = useJobUtils();

  const getClientName = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || clientId;
  };

  const getClassificationGroupName = (job: Job) => {
    const classificationId = job.classification?.id ?? job.classificationId;
    if (!classificationId) return '-';
    const classification = getClassificationById(classificationId);
    if (!classification?.classificationGroupId) return '-';
    const group = classificationGroups.find(
      (g) => g.id === classification.classificationGroupId,
    );
    return group?.name || '-';
  };

  // Column configuration - matching screenshot columns
  const columns = [
    {
      key: 'status',
      title: 'Status',
      width: 120,
      sortable: true,
      renderCell: (job: Job) => (
        <span
          className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(
            job.itemStatus || undefined,
          )}`}
        >
          {job.itemStatus || ''}
        </span>
      ),
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'candidate',
      title: getTerm(TemplateTerm.WORKER, false),
      width: 180,
      sortable: true,
      renderCell: (job: Job) => {
        const worker = job.userId ? getWorkerById(job.userId) : null;
        if (worker) {
          return (
            <div className="text-sm text-gray-900">
              {worker.firstName || ''} {worker.lastName || ''}
            </div>
          );
        }
        return <div className="text-sm text-gray-900">-</div>;
      },
      cellClassName: 'px-4 py-3 overflow-hidden',
      conditional: showCandidateColumn,
    },
    {
      key: 'skillSet',
      title: 'Skill',
      width: 180,
      sortable: false,
      renderCell: (job: Job) => (
        <div className="flex items-center gap-1">
          {job.classification?.name && (
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800">
              {job.classification.name}
            </span>
          )}
        </div>
      ),
      cellClassName: 'px-4 py-3 overflow-hidden',
    },
    {
      key: 'client',
      title: getTerm(TemplateTerm.CLIENT, false),
      width: 180,
      sortable: true,
      renderCell: (job: Job) => (
        <div
          className="truncate text-sm text-gray-900"
          title={getClientName(job.clientId)}
        >
          {getClientName(job.clientId)}
        </div>
      ),
      cellClassName: 'px-4 py-3 overflow-hidden',
      conditional: showClientColumn,
    },
    {
      key: 'site',
      title: 'Site',
      width: 180,
      sortable: true,
      renderCell: (job: Job) => (
        <div
          className="truncate text-sm text-gray-900"
          title={job.site?.name || undefined}
        >
          {job.site?.name || '-'}
        </div>
      ),
      cellClassName: 'px-4 py-3 overflow-hidden',
    },
    {
      key: 'startTime',
      title: 'start time',
      width: 120,
      sortable: true,
      renderCell: (job: Job) => {
        const time = job.days?.startTime || job.clockInTime;
        const formatted = formatJobTime12h(time);
        return <div className="text-sm text-gray-900">{formatted ?? '-'}</div>;
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'endTime',
      title: 'end time',
      width: 120,
      sortable: true,
      renderCell: (job: Job) => {
        const time = job.days?.endTime || job.clockOutTime;
        const formatted = formatJobTime12h(time);
        return <div className="text-sm text-gray-900">{formatted ?? '-'}</div>;
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'startDate',
      title: 'start date',
      headerTitle:
        'First calendar day of the job schedule (from shift pattern, not raw contract timestamp).',
      width: 168,
      sortable: true,
      renderCell: (job: Job) => {
        const d = getJobDisplayScheduleStartDay(job);
        return (
          <div className="text-sm text-gray-900">
            {d ? d.format('D MMM YYYY') : '-'}
          </div>
        );
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'endDate',
      title: 'end date',
      headerTitle:
        'Last calendar day of the job schedule (overnight shifts use the working day, not the morning-after contract end).',
      width: 168,
      sortable: true,
      renderCell: (job: Job) => {
        const d = getJobDisplayScheduleEndDay(job);
        return (
          <div className="text-sm text-gray-900">
            {d ? d.format('D MMM YYYY') : '-'}
          </div>
        );
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'margin',
      title: 'Margin ($)',
      width: 120,
      sortable: false,
      renderCell: (job: Job) => (
        <div className="text-sm text-gray-900">
          {job.marginPercentage ? `${job.marginPercentage}%` : '-'}
        </div>
      ),
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
      conditional: showMarginColumn,
    },
    {
      key: 'industry',
      title: 'Industry',
      width: 150,
      sortable: false,
      renderCell: (job: Job) => {
        const name = getClassificationGroupName(job);
        return (
          <div
            className="truncate text-sm text-gray-900"
            title={name !== '-' ? name : undefined}
          >
            {name}
          </div>
        );
      },
      cellClassName: 'px-4 py-3 overflow-hidden',
    },
    {
      key: 'break',
      title: 'Break',
      width: 120,
      sortable: false,
      renderCell: (job: Job) => {
        const breakMinutes =
          job.paidBreakDurationMinutes || job.unpaidBreakDurationMinutes || 0;
        return (
          <div className="text-sm text-gray-900">
            {breakMinutes > 0 ? `${breakMinutes} mins` : '-'}
          </div>
        );
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'jobDuration',
      title: 'Job duration',
      width: 140,
      sortable: false,
      renderCell: (job: Job) => {
        if (job.durationInDays) {
          const weeks = Math.floor(job.durationInDays / 7);
          const days = job.durationInDays % 7;
          if (weeks > 0 && days === 0) {
            return (
              <div className="text-sm text-gray-900">
                {weeks} {weeks === 1 ? 'week' : 'weeks'}
              </div>
            );
          } else if (weeks > 0) {
            return (
              <div className="text-sm text-gray-900">
                {weeks} {weeks === 1 ? 'week' : 'weeks'} {days}{' '}
                {days === 1 ? 'day' : 'days'}
              </div>
            );
          } else {
            return (
              <div className="text-sm text-gray-900">
                {job.durationInDays} {job.durationInDays === 1 ? 'day' : 'days'}
              </div>
            );
          }
        }
        return <div className="text-sm text-gray-900">-</div>;
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'postedDate',
      title: 'Posted date',
      width: 140,
      sortable: true,
      renderCell: (job: Job) => (
        <div className="text-sm text-gray-900">
          {job.createdAt
            ? dayjs(parseInt(job.createdAt)).format('D MMM YYYY')
            : '-'}
        </div>
      ),
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
    },
    {
      key: 'applications',
      title: 'Applications',
      width: 120,
      sortable: false,
      renderCell: (job: Job) => {
        const count =
          job.applicationCount == null ? null : Number(job.applicationCount);
        const display =
          count == null || !Number.isFinite(count) ? '-' : String(count);
        return <div className="text-sm text-gray-900">{display}</div>;
      },
      cellClassName: 'px-4 py-3 whitespace-nowrap overflow-hidden',
      conditional: showApplicationsColumn,
    },
  ];

  // Build defaultWidths from column config
  const defaultWidths = columns.reduce(
    (acc, col) => {
      acc[col.key] = col.width;
      return acc;
    },
    {} as Record<string, number>,
  );

  const { columnWidths, handleMouseDown } = useColumnResize({
    defaultWidths,
    minWidth: 80,
  });

  // Filter columns based on conditional flag
  const visibleColumns = columns.filter(
    (col) => col.conditional === undefined || col.conditional === true,
  );

  const tableRef = useRef<HTMLTableElement>(null);
  const hasFetchedWorkersRef = useRef(false);

  // Fetch workers if assignee names are missing from the cache
  useEffect(() => {
    const fetchMissingWorkers = async () => {
      if (!showCandidateColumn || hasFetchedWorkersRef.current) return;

      const userIds = new Set<string>();
      jobs.forEach((job) => {
        if (job.userId) {
          userIds.add(job.userId);
        }
      });

      const hasMissingWorkers = Array.from(userIds).some(
        (userId) => !workers?.find((w) => w?.id === userId),
      );

      if (!hasMissingWorkers) {
        return;
      }

      hasFetchedWorkersRef.current = true;
      try {
        if (workersClientId) {
          await fetchWorkersForClient(workersClientId);
          return;
        }
        if (tenantId) {
          await fetchWorkersForTenant(tenantId);
        }
      } catch (error) {
        console.error('Error fetching workers for jobs table:', error);
      }
    };

    void fetchMissingWorkers();
  }, [
    jobs,
    showCandidateColumn,
    workersClientId,
    tenantId,
    fetchWorkersForClient,
    fetchWorkersForTenant,
    workers,
  ]);

  // Reset fetch flag when scope changes
  useEffect(() => {
    hasFetchedWorkersRef.current = false;
  }, [tenantId, workersClientId]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
        <table
          ref={tableRef}
          className="w-full border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            {visibleColumns.map((col) => (
              <col
                key={col.key}
                style={{ width: `${columnWidths[col.key]}px` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  title={
                    (col as { headerTitle?: string }).headerTitle ?? undefined
                  }
                  onClick={() => col.sortable && onSort(col.key as SortField)}
                  className={`relative px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase hover:bg-gray-50 ${
                    col.sortable ? 'cursor-pointer' : ''
                  }`}
                  style={{ width: `${columnWidths[col.key]}px` }}
                >
                  <div className="flex items-center">
                    {col.title}
                    {col.sortable && (
                      <span className="ml-2 text-gray-400">
                        {getSortIcon(col.key as SortField)}
                      </span>
                    )}
                  </div>
                  <ResizeHandle
                    column={col.key}
                    onMouseDown={handleMouseDown}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id} className="hover:bg-gray-50">
                {visibleColumns.map((col) => (
                  <td key={col.key} className="p-0 align-middle">
                    <Link
                      to={`/jobs/${job.id}`}
                      onClick={(e) => {
                        if (shouldUseDefaultLinkNavigation(e)) {
                          return;
                        }
                        e.preventDefault();
                        onJobClick(job.id);
                      }}
                      className={`${col.cellClassName} block w-full max-w-full cursor-pointer text-inherit no-underline visited:text-inherit`}
                    >
                      {col.renderCell(job)}
                    </Link>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {pagination.endIndex ? pagination.startIndex : 0} to{' '}
          {pagination.endIndex} of {pagination.totalItems} results
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || isLoading}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => {
              pagination.onPageChange(pagination.currentPage + 1);
            }}
            disabled={
              (pagination.currentPage >= pagination.totalPages &&
                !pagination.hasMore) ||
              isLoading
            }
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsTable;
